import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { sort } from '@ember/object/computed';
import LoginController from 'cio/pods/login/controller';
import { ChooseUserEntryDataObject } from 'cio/authenticators/login-google-one-click';
import { task } from 'ember-concurrency';
import { IdentityProvider } from 'cio/models/sso-configuration';
import SsoService from 'cio/services/sso';

type FormattedChooseUserEntryDataObject = ChooseUserEntryDataObject & {
  hasGoogleSSODomainMismatch: boolean;
};

export default class LoginChooseUser extends Controller {
  @service sso!: SsoService;

  defaultAccountLogoImage = new URL('cio/assets/images/default-account-logo.svg', import.meta.url).toString();

  @controller('login') loginController!: LoginController;

  @action
  isOtherThanGoogleSSO(idProvider: IdentityProvider | '') {
    return idProvider !== '' && idProvider !== IdentityProvider.GoogleOIDC;
  }

  @computed('loginController.googleOneClickGoogleAccountEmail')
  get googleOneClickEmailDomain() {
    return this.loginController.googleOneClickGoogleAccountEmail?.split('@')[1];
  }

  @sort('formattedUsersData', function (a: FormattedChooseUserEntryDataObject, b: FormattedChooseUserEntryDataObject) {
    // 1. show accessible accounts (with "hasGoogleSSODomainMismatch === false") first:
    if (a.hasGoogleSSODomainMismatch && !b.hasGoogleSSODomainMismatch) {
      return 100;
    } else if (!a.hasGoogleSSODomainMismatch && b.hasGoogleSSODomainMismatch) {
      return -100;
    }

    // 2. alphabetic sorting by e-mail, with priority for un-aliased e-mails (no "+") character
    //
    // replace '+' with '`' (back-tick) before sorting to show unaliased e-mails first
    // - "+" sorts before "@", so "ami+alias@customer.io" would come before "ami@customer.io"
    // - but "`" sorts after "@", so "ami`alias@customer.io" comes AFTER un-aliased "ami@customer.io"
    let emailA = a.userEmail.replace(/\+/g, '`').toLowerCase();
    let emailB = b.userEmail.replace(/\+/g, '`').toLowerCase();
    if (emailA < emailB) {
      return -1;
    } else if (emailA > emailB) {
      return 1;
    }

    return 0;
  })
  sortedFormattedUsersData!: FormattedChooseUserEntryDataObject[];

  @computed('loginController.googleOneClickChooseUserEntries.[]')
  get formattedUsersData(): FormattedChooseUserEntryDataObject[] | undefined {
    return this.loginController.googleOneClickChooseUserEntries?.map((u) => ({
      ...u,
      // whether to block login based on Google SSO domain mis-match:
      // Google SSO must be on AND none of user's external identity email (Google account) and CIO email must the Google SSO domain:
      hasGoogleSSODomainMismatch: Boolean(
        u.accountSSOOIDCGoogleHostedDomain && //
          (u.userExternalIdentityEmail || u.userEmail).split('@')[1] !== u.accountSSOOIDCGoogleHostedDomain,
      ),
    }));
  }

  userAccountChoiceTask = task({ drop: true }, async (userData: FormattedChooseUserEntryDataObject) => {
    if (userData.hasGoogleSSODomainMismatch) {
      // Google SSO with mis-matching domains: don't do anything (shows up as disabled in UI)

      return;
    } else if (this.isOtherThanGoogleSSO(userData.accountSSOProvider)) {
      // Non-Google SSO: fetch SSO configuration info and open auth pop-up, login

      let { ssoConfiguration } = await this.sso.fetchSSOInfo({ email: userData.userEmail });

      this.loginController.setProperties({
        ssoConfigurationForLogin: ssoConfiguration,
        email: userData.userEmail,
      });
      await this.loginController.loginTaskSSO.perform();
    } else {
      // Google SSO or no SSO: finish login
      await this.loginController.loginTaskGoogleOneClickChooseUserFinish.perform(userData.userID);
    }
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'login/choose_user': LoginChooseUser;
  }
}
